import ParlorExitDrawer from "../components/ParlorExitDrawer";
import FullscreenDrawer from "@/components/common/Drawer/OverlayDrawer/FullscreenDrawer";
import FullscreenDrawerContent from "@/components/common/Drawer/FullscreenDrawerContent";
import { useDialog } from "@/hooks/useDialog";
import { useOverlay } from "@toss/use-overlay";
import { customEvent } from "goi_common";
import Router, { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";

// parlor-requests에서 정상 이동인 경로 - 모달 노출 X
const isWhitelistedRoute = (url: string): boolean => {
  const pathOnly = url.split("?")[0];
  if (pathOnly === "/match/request-complete/") return true;
  // 제출한 회원이 곧장 가는 결과 페이지. 정상 이동이라 이탈 방지 대상이 아니다.
  if (pathOnly === "/match/recommendation/") return true;
  if (pathOnly === "/login/") return true;
  if (pathOnly === "/funeral-prepare/company-introduction/") return true;
  if (pathOnly === "/review/") return true;
  return false;
};

/**
 * 의도된 이동 플래그.
 *
 * `isWhitelistedRoute` 는 **Next 클라이언트 라우팅**(routeChangeStart)에만 걸린다.
 * `beforeunload` 에는 화이트리스트가 없어서, 같은 목적지라도 **풀 페이지 이동**으로 일어나면
 * 브라우저 확인창("사이트를 다시 로드/나가시겠습니까?")이 그대로 뜬다.
 *
 * 풀 페이지 이동은 의도적으로 일어나기도 하고(`tel:`, 외부 인증), Next 가 라우팅에 실패해
 * `window.location` 폴백을 탈 때도 일어난다(청크 로드 실패 등 - "가끔" 뜨는 이유).
 * 정상 전환인 것을 아는 지점에서 이 플래그를 세워 확인창을 건너뛴다.
 */
let isIntentionalLeave = false;

/** 정상 전환(제출 후 로그인 이동, 통화 연결 등) 직전에 호출한다. */
export const allowIntentionalLeave = () => {
  isIntentionalLeave = true;
};

/**
 * A/B 공통 뒤로가기 이탈 방지 표식. 첫 단계를 넘기면 이 쿼리로 사이트 안
 * 기록을 한 칸 쌓는다. 그 칸을 빠져나가는 뒤로가기에서 모달을 띄운다.
 */
const BACK_TRAP_PARAM = "started";

const hasStartedParam = (url: string): boolean =>
  new URLSearchParams(url.split("#")[0].split("?")[1] ?? "").has(BACK_TRAP_PARAM);

// 훅 밖(StepByStep 클릭 핸들러)에서 무장하려고 모듈 스코프에 둔다. isIntentionalLeave 와 같은 방식.
let backTrapArmable = false;

/**
 * 첫 단계를 넘기는 **사용자 클릭 처리 안에서 동기로** 호출한다(setTimeout 안 X).
 * Android Chrome 은 사용자 동작 없이 쌓인 기록을 뒤로가기에서 건너뛴다.
 * 견적 페이지가 마운트되지 않았거나 이미 표식이 있으면 아무 일도 하지 않는다.
 */
export const armBackTrap = () => {
  if (!backTrapArmable || BACK_TRAP_PARAM in Router.query) return;
  Router.push(
    { pathname: Router.pathname, query: { ...Router.query, [BACK_TRAP_PARAM]: "1" } },
    undefined,
    { shallow: true, scroll: false },
  );
};

/**
 * @param backTrapB 새 안내 모달 여부. A는 기존 창, B는 새 창이며 뒤로가기 처리는 공통이다.
 */
export default function useParlorRouterChangeLoad(backTrapB = false) {
  const router = useRouter();
  const { onOpen, onClose } = useDialog();
  const [confirmed, setConfirmed] = useState(false);
  const [toUrl, setToUrl] = useState("");
  // 표식 칸 이탈로 뜬 모달이면 그만두기가 router.replace 가 아니라 기록 되돌리기여야 한다
  const [exitByHistory, setExitByHistory] = useState(false);
  const isBackRef = useRef(false);
  const checklistOverlay = useOverlay();
  const exitOverlay = useOverlay();

  // 페이지에 다시 진입하면 이탈 방지를 되살린다. 모듈 스코프 플래그라 SPA 세션 내내
  // 남아 있으면 이후 방문에서 방지가 조용히 꺼진다.
  useEffect(() => {
    isIntentionalLeave = false;
  }, []);

  useEffect(() => {
    backTrapArmable = true;
    // 뒤로가기(popstate)와 링크 클릭을 구분한다. routeChangeStart 가 소비하고 비운다.
    // ponytail: 해시만 바뀌는 뒤로가기는 routeChangeStart 가 없어 플래그가 남을 수 있다. 이 페이지엔 해시 이동 없음.
    Router.beforePopState(() => {
      isBackRef.current = true;
      return true;
    });
    return () => {
      backTrapArmable = false;
      Router.beforePopState(() => true);
    };
  }, []);

  const handleBeforeunload = (e: BeforeUnloadEvent) => {
    if (isIntentionalLeave) return;
    e.preventDefault();
    e.returnValue = "";
    return "";
  };

  const openChecklistDrawer = () => {
    checklistOverlay.open(({ isOpen, close: onOverlayClose }) => {
      const handleClose = () => {
        onOverlayClose();
        setConfirmed(true);
      };
      return (
        <FullscreenDrawer
          onClose={() => {
            customEvent({
              newGtm: {
                path: "parlor_req_quit",
                trigger_and_target: "drawer_close",
              },
            });
            handleClose();
          }}
          isOpen={isOpen}
          drawerContentStyle={{
            aspectRatio: 450 / 300,
            width: "90%",
            margin: "auto",
            height: "90%",
            maxHeight: "600px",
            maxWidth: "360px",
          }}
          body={
            <FullscreenDrawerContent
              onClose={handleClose}
              onSuccess={handleClose}
              path="parlor_req_quit"
              eventPath="parlor_req_quit"
            />
          }
        />
      );
    });
  };

  const routeChangeStart = useCallback(
    (url: string) => {
      const isBack = isBackRef.current;
      isBackRef.current = false;

      if (isIntentionalLeave || isWhitelistedRoute(url)) return;
      // 첫 문항을 진행한 고객이 표식 칸(started=1)에서 표식 없는 같은 경로로 빠져나가는 뒤로가기는
      // 같은 경로여도 모달 대상이다. 앞으로 가는 표식 push 는 isBack 이 아니라 여기 안 걸린다.
      const isTrapExit = isBack && hasStartedParam(router.asPath) && !hasStartedParam(url);
      if (!isTrapExit && router.asPath.split("?")[0] === url.split("?")[0]) return;
      if (confirmed) return;

      // 뒤로가기 누른 시점의 scroll 위치 기억. 브라우저가 비동기적으로 직전 페이지의
      // 저장된 scroll 위치를 복원하려 하므로, 이를 무력화하기 위해 원위치로 강제 복원.
      const preservedScrollY = window.scrollY;

      // 뒤로가기 시 URL 동기화 (모달 띄우는 동안 주소 표시 일관성)
      if (router.asPath !== window.location.pathname) {
        window.history.pushState("", "", router.asPath);
      }

      setToUrl(url);
      setExitByHistory(isTrapExit);

      customEvent({
        newGtm: {
          path: "parlor_match_request",
          trigger_and_target: "open_b4_modal",
        },
      });

      if (backTrapB) {
        exitOverlay.open(({ isOpen, close }) => (
          <ParlorExitDrawer
            isOpen={isOpen}
            onClose={() => {
              customEvent({
                newGtm: { path: "parlor_match_request", trigger_and_target: "b4_modal_cont" },
              });
              close();
            }}
            onNavigate={(destination, event) => {
              customEvent({ newGtm: { path: "parlor_match_request", trigger_and_target: event } });
              close();
              setExitByHistory(false);
              setToUrl(destination);
              setConfirmed(true);
            }}
            onQuit={() => {
              customEvent({
                newGtm: { path: "parlor_match_request", trigger_and_target: "b4_modal_quit" },
              });
              close();
              setConfirmed(true);
            }}
          />
        ));
      } else
        onOpen({
          type: 1,
          header: "거의 다 왔어요",
          footerProps: [
            {
              text: "그만두기",
              onClick: () => {
                customEvent({
                  newGtm: {
                    path: "parlor_match_request",
                    trigger_and_target: "b4_modal_quit",
                  },
                });
                onClose();
                openChecklistDrawer();
              },
            },
            {
              text: "계속하기",
              onClick: () => {
                customEvent({
                  newGtm: {
                    path: "parlor_match_request",
                    trigger_and_target: "b4_modal_cont",
                  },
                });
                onClose();
              },
            },
          ],
          children: (
            <>
              견적을 제출한 고객님들은
              <br />
              장례 비용을 평균 246만원 아끼고 있어요
            </>
          ),
        });

      // 브라우저의 비동기 scroll restoration을 override.
      // 단일 rAF는 브라우저 restoration이 같은 frame에 늦게 실행되면 무력화되므로,
      // 두 번째 rAF로 다음 frame에 한 번 더 복원해 안정성 확보.
      requestAnimationFrame(() => {
        window.scrollTo(0, preservedScrollY);
        requestAnimationFrame(() => {
          window.scrollTo(0, preservedScrollY);
        });
      });

      router.events.emit("routeChangeError");
      throw "Abort route change. Please ignore this error.";
    },
    [backTrapB, confirmed, router.asPath, router.events, onOpen, onClose],
  );

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeunload);
    router.events.on("routeChangeStart", routeChangeStart);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeunload);
      router.events.off("routeChangeStart", routeChangeStart);
    };
  }, [routeChangeStart, router.events]);

  useEffect(() => {
    if (confirmed) {
      onClose();
      if (exitByHistory) {
        // 스택은 [직전 사이트, 표식 없는 칸, 표식 칸] 이다(모달이 표식 칸을 다시 push 하므로 항상 같다).
        // 같은 페이지로 replace 하면 안 나가진다. 직전 사이트까지 두 칸 되돌린다.
        // beforeunload 확인창이 한 번 더 뜨지 않게 의도된 이동으로 표시한다.
        allowIntentionalLeave();
        window.history.go(-2);
      } else {
        router.replace(toUrl);
      }
    }
  }, [confirmed]);
}
